import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const RGPDConsentNotification = ({ onRenewConsent, onDismiss }) => {
  const [consentStatus, setConsentStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const checkConsentStatus = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;

        const response = await axios.get(`${API}/client/rgpd/consent-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setConsentStatus(response.data);
      } catch (error) {
        console.error('Error checking consent status:', error);
      } finally {
        setLoading(false);
      }
    };

    checkConsentStatus();
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in sessionStorage (will reappear on next session)
    if (consentStatus?.status === 'expiring_soon') {
      sessionStorage.setItem('rgpd_warning_dismissed', 'true');
    }
    if (onDismiss) onDismiss();
  };

  const handleRenew = () => {
    if (onRenewConsent) onRenewConsent();
  };

  // Check if warning was dismissed this session
  useEffect(() => {
    if (consentStatus?.status === 'expiring_soon') {
      const wasDismissed = sessionStorage.getItem('rgpd_warning_dismissed');
      if (wasDismissed === 'true') {
        setDismissed(true);
      }
    }
  }, [consentStatus]);

  if (loading || dismissed) return null;
  
  // Don't show notification if consent is valid and not expiring soon
  if (!consentStatus || consentStatus.status === 'valid') return null;

  // Determine notification style based on status
  const getNotificationStyle = () => {
    switch (consentStatus.status) {
      case 'expired':
      case 'no_consent':
        return {
          backgroundColor: 'rgba(255, 87, 87, 0.15)',
          borderColor: '#FF5757',
          iconColor: '#FF5757',
          icon: '🚨'
        };
      case 'version_outdated':
        return {
          backgroundColor: 'rgba(0, 194, 255, 0.15)',
          borderColor: '#00C2FF',
          iconColor: '#00C2FF',
          icon: '📋'
        };
      case 'expiring_soon':
        return {
          backgroundColor: 'rgba(255, 184, 0, 0.15)',
          borderColor: '#FFB800',
          iconColor: '#FFB800',
          icon: '⏰'
        };
      default:
        return {
          backgroundColor: 'rgba(255, 184, 0, 0.15)',
          borderColor: '#FFB800',
          iconColor: '#FFB800',
          icon: '⚠️'
        };
    }
  };

  const style = getNotificationStyle();
  const canDismiss = consentStatus.status === 'expiring_soon';

  return (
    <div
      style={{
        backgroundColor: style.backgroundColor,
        border: `1px solid ${style.borderColor}`,
        borderRadius: '12px',
        padding: '16px 20px',
        marginBottom: '20px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        animation: 'fadeIn 0.3s ease-in-out'
      }}
    >
      <span style={{ fontSize: '24px', flexShrink: 0 }}>{style.icon}</span>
      
      <div style={{ flex: 1 }}>
        <p style={{ 
          color: style.iconColor, 
          fontWeight: '600', 
          fontSize: '15px',
          marginBottom: '4px'
        }}>
          {consentStatus.status === 'expired' && 'Consentement RGPD expiré'}
          {consentStatus.status === 'no_consent' && 'Consentement RGPD requis'}
          {consentStatus.status === 'version_outdated' && 'Nouvelle politique de confidentialité'}
          {consentStatus.status === 'expiring_soon' && 'Consentement RGPD bientôt expiré'}
        </p>
        <p style={{ 
          color: '#B8C0E0', 
          fontSize: '13px',
          lineHeight: '1.5'
        }}>
          {consentStatus.message}
        </p>
        
        {consentStatus.days_remaining !== null && consentStatus.days_remaining > 0 && (
          <p style={{ 
            color: '#8A92B2', 
            fontSize: '12px',
            marginTop: '8px'
          }}>
            Date d&apos;expiration : {new Date(consentStatus.expiration_date).toLocaleDateString('fr-FR')}
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
        {consentStatus.requires_renewal && (
          <button
            onClick={handleRenew}
            style={{
              backgroundColor: '#00FFB3',
              color: '#1A1E2E',
              border: 'none',
              borderRadius: '8px',
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#00E6A0';
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#00FFB3';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            Renouveler
          </button>
        )}
        
        {canDismiss && (
          <button
            onClick={handleDismiss}
            style={{
              backgroundColor: 'transparent',
              color: '#8A92B2',
              border: '1px solid #3A4359',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#5B638C';
              e.currentTarget.style.color = '#B8C0E0';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#3A4359';
              e.currentTarget.style.color = '#8A92B2';
            }}
            title="Rappeler plus tard"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

export default RGPDConsentNotification;
